import { Add as AddIcon } from '@mui/icons-material';
import { Alert, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { getAllFunnels } from 'API/linkedin-funnel';
import { ProductGroups } from 'enums';
import { Funnel, FunnelStatuses, FunnelTypes } from 'features/co-pilot/co-pilot.types';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import PaiAutoComplete, { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiIconButton from 'features/ui/base-components/pai-icon-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { WebmeticsMapping } from './webmetics.types';

interface CreateWebmeticsMappingDialogProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (submission: Partial<WebmeticsMapping>) => void;
  initialMapping?: WebmeticsMapping | null;
  productGroup: ProductGroups;
}

const CreateWebmeticsMappingDialog: React.FC<CreateWebmeticsMappingDialogProps> = ({
  open,
  handleClose,
  onSubmit,
  initialMapping = null,
  productGroup,
}) => {
  const { classes } = useStyles();
  const { token } = useUserData();
  const { t } = useTranslation();

  const [mappingId, setMappingId] = useState(initialMapping?._id || '');
  const [schemaName, setSchemaName] = useState(initialMapping?.schemaName || '');
  const [apiKeyWebmetics, setApiKeyWebmetics] = useState(initialMapping?.apiKeyWebmetics || '');

  // Funnel/Campaign selection states
  const [selectedCampaignOption, setSelectedCampaignOption] = useState<OptionType | null>(null);
  const [campaignOptions, setCampaignOptions] = useState<OptionType[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState<boolean>(false);

  const [domain, setDomain] = useState(initialMapping?.domain || '');
  const [occupations, setOccupations] = useState<string[]>(initialMapping?.occupations || []);
  const [currentOccupation, setCurrentOccupation] = useState<string>('');
  const [visitedWebsites, setVisitedWebsites] = useState<string[]>(initialMapping?.visitedWebsites || []);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const type =
    productGroup === ProductGroups.HR_BOOSTER
      ? FunnelTypes.ACTIVE_SOURCING_CO_PILOT
      : productGroup === ProductGroups.LEAD_BOOSTER
      ? FunnelTypes.SOCIAL_SELLING_CO_PILOT
      : undefined;

  const fetchFunnels = async () => {
    if (!token || !type) return;
    setLoadingCampaigns(true);
    // Consider if errorMessage for funnels should be separate from general dialog error
    try {
      const res = await getAllFunnels(
        type,
        undefined, // q - search term
        undefined, // page
        undefined, // total (limit)
        undefined, // filter (active/inactive)
        undefined, // linkedinAccountFilter
        undefined, // campaignStatusFilter
        false // basicInfo
      );
      if (res && !res.error) {
        const resCampaigns: Funnel[] = res.campaigns || [];
        const filteredCampaigns = resCampaigns.filter(
          (campaign) => campaign.status !== FunnelStatuses.ARCHIVED // Allow CREATED, only filter out ARCHIVED
        );
        const mappedOptions: OptionType[] = filteredCampaigns.map((funnel) => ({
          value: funnel._id,
          label: funnel.name,
        }));
        setCampaignOptions(mappedOptions);
      } else {
        setCampaignOptions([]);
        setErrorMessage(res.error?.message || 'Failed to fetch campaigns.');
      }
    } catch (error: any) {
      console.error('Error fetching campaigns:', error);
      setCampaignOptions([]);
      setErrorMessage(error.message || 'An unexpected error occurred while fetching campaigns.');
    } finally {
      setLoadingCampaigns(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFunnels();
    } else {
      setSelectedCampaignOption(null);
      setCampaignOptions([]);
    }
  }, [open, token, type]);

  useEffect(() => {
    if (initialMapping) {
      setMappingId(initialMapping._id || '');
      setSchemaName(initialMapping.schemaName);
      setApiKeyWebmetics(initialMapping.apiKeyWebmetics);

      setDomain(initialMapping.domain || '');
      setOccupations(initialMapping.occupations || []);
      setVisitedWebsites(initialMapping.visitedWebsites || []);
      // Attempt to set initial displayed name if possible (might need adjustment)
      if (initialMapping.funnelId && campaignOptions.length > 0) {
        const currentFunnel = campaignOptions.find((c) => c.value === initialMapping.funnelId);
        if (currentFunnel) {
          setSelectedCampaignOption(currentFunnel);
        }
      }
    } else {
      setMappingId('');
      setSchemaName('');
      setApiKeyWebmetics('');
      setSelectedCampaignOption(null);
      setDomain('');
      setOccupations([]);
      setVisitedWebsites([]);
      setCurrentOccupation('');
    }
  }, [initialMapping, open, campaignOptions, loadingCampaigns]); // Added loadingCampaigns dependency

  const handleCampaignChange = (event: React.ChangeEvent<HTMLInputElement>, newValue: OptionType | null) => {
    setSelectedCampaignOption(newValue);
  };

  const handleAddOccupation = () => {
    if (currentOccupation.trim() !== '' && !occupations.includes(currentOccupation.trim())) {
      setOccupations([...occupations, currentOccupation.trim()]);
      setCurrentOccupation('');
    }
  };

  const handleRemoveOccupation = (occupationToRemove: string) => {
    setOccupations(occupations.filter((occupation) => occupation !== occupationToRemove));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    try {
      if (!schemaName.trim()) throw new Error('Schema Name is required.');
      if (!apiKeyWebmetics.trim()) throw new Error('API Key for Webmetics is required.');
      if (!selectedCampaignOption) throw new Error('Campaign selection is required.');
      if (!domain.trim()) throw new Error('Domain is required.');

      const submission: Partial<WebmeticsMapping> = {
        schemaName: schemaName.trim(),
        apiKeyWebmetics: apiKeyWebmetics.trim(),
        funnelId: selectedCampaignOption.value,
        domain: domain.trim(),
        occupations,
        visitedWebsites,
      };

      await onSubmit(submission);
    } catch (error: any) {
      console.error('Error saving Webmetics mapping:', error);
      setErrorMessage(error.message || 'Failed to save mapping.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'sm'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t(mappingId ? 'updateWebmeticsMappingTitle' : 'createWebmeticsMappingTitle')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack direction={'column'} gap={2}>
          {errorMessage && (
            <Alert className={classes.alert} severity="error" onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}

          <PaiTextField
            label={t('schemaName')}
            placeholder={t('enterSchemaName')}
            value={schemaName}
            onChange={(e) => setSchemaName(e.target.value)}
            disabled={isSaving}
          />
          <PaiTextField
            label={t('apiKeyWebmetics')}
            placeholder={t('enterApiKey')}
            value={apiKeyWebmetics}
            onChange={(e) => setApiKeyWebmetics(e.target.value)}
            disabled={isSaving}
          />

          <Stack direction="row" gap={1} alignItems="center">
            <PaiAutoComplete
              label={t('campaign')}
              placeholder={t('selectCampaign')}
              options={campaignOptions}
              selected={selectedCampaignOption}
              disabled={loadingCampaigns}
              onChange={handleCampaignChange}
            />
            {loadingCampaigns && (
              <span style={{ marginTop: '25px' }}>
                <PaiSpinningLoader size="small" inline={true} />
              </span>
            )}
          </Stack>

          <PaiTextField
            label={t('domain')}
            placeholder={t('enterDomain')}
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            disabled={isSaving}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <PaiTextField
              label={t('occupation')}
              placeholder={t('enterOccupation')}
              value={currentOccupation}
              onChange={(e) => setCurrentOccupation(e.target.value)}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAddOccupation();
                }
              }}
              disabled={isSaving}
            />
            <span style={{ marginTop: '25px' }}>
              <PaiIconButton size="small" onClick={handleAddOccupation} disabled={!currentOccupation.trim() || isSaving}>
                <AddIcon />
              </PaiIconButton>
            </span>
          </Stack>

          {!_.isEmpty(occupations) && (
            <Stack direction="row" gap={1} flexWrap="wrap">
              {occupations.map((occupation, index) => (
                <Chip
                  key={index}
                  className={classes.chip}
                  label={occupation}
                  variant={'outlined'}
                  size={'small'}
                  onDelete={() => handleRemoveOccupation(occupation)}
                  disabled={isSaving}
                  style={{ borderRadius: '4px' }}
                />
              ))}
            </Stack>
          )}

          <Divider />
          <PaiTextField
            label={t('visitedWebsitesCommaSeparated')}
            placeholder={t('enterVisitedWebsites')}
            value={visitedWebsites.join(', ')}
            onChange={(e) => setVisitedWebsites(e.target.value.split(',').map((item) => item.trim()))}
            disabled={isSaving}
          />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} disabled={isSaving} />
        <PaiButton
          text={t(mappingId ? 'updateMapping' : 'saveMapping')}
          size={'small'}
          border={'rounded'}
          action={handleSave}
          loading={isSaving}
          disabled={loadingCampaigns && !selectedCampaignOption}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
  alert: {
    borderRadius: '8px',
  },
  chip: {
    borderColor: theme.palette.greys.light,
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root:hover': {
      color: theme.palette.secondary.main,
    },
  },
}));

export default CreateWebmeticsMappingDialog;
